<template>
  <client-only>
    <div
      v-if="header.showSavePanel || header.showCartPanel || header.showHelpPanel"
      class="overlay"
      @click="hidePanels"
    >
      &nbsp;
    </div>
    <div class="appHeader flex-column gap8">
      <div class="flex-row align-items-center justify-content-space-between">
        <div style="width: 180px; height: 44px">
          <img
            v-if="header.logoBigUrl"
            class="logo-big"
            :src="header.logoBigUrl"
            alt="logo"
          >
        </div>
        <div
          v-if="showProgressBar || (limitHeader && limitPositionNumber > 0)"
          id="upperProgress"
          class="flex-column align-items-center gap8 grow"
        >
          <template v-if="isPrescore || isB2bPrescore">
            <p class="p-small">
              {{ $t("bnpl:step", { current: prescoreCurrentStep, count: prescoreStepCount }) }}
              ({{ formatStepTime(currentStepDuration, countryStore.locale) }})
            </p>
            <div style="width: 100%; max-width: 612px">
              <ProgressBar
                :max="prescoreStepCount"
                :value="prescoreCurrentStep"
              />
            </div>
          </template>
          <template v-else-if="limitHeader">
            <p class="p-small">
              {{ $t("bnpl:step", { current: limitPositionNumber, count: 6 }) }}
            </p>
            <div style="width: 100%; max-width: 612px">
              <ProgressBar
                :max="6"
                :value="limitPositionNumber"
              />
            </div>
          </template>
          <template v-else>
            <p class="p-small">
              {{
                $t("bnpl:step", {
                  current: currentStep,
                  count: stepCount,
                })
              }}
              <span v-if="currentStepDuration">
                ({{ formatStepTime(currentStepDuration, countryStore.locale) }})
              </span>
            </p>
            <div style="width: 100%; max-width: 612px">
              <ProgressBar
                :max="stepCount"
                :value="currentStep"
              />
            </div>
          </template>
        </div>
        <div class="flex-row align-items-end padding0 menuButtonContainer">
          <template v-if="!isPrescore && !limitHeader && !isManagerKyc && !inCustomerLimit">
            <button
              v-if="showCartButton"
              class="menuButton"
              type="button"
              @click="toggleCart"
            >
              <IllustrationsMenuButtonCartOff
                v-if="!header.showCartPanel"
                :cart-size="header.cart.items ? header.cart.items.length : 0"
              />
              <IllustrationsMenuButtonCartOn
                v-else
                :cart-size="header.cart.items ? header.cart.items.length : 0"
              />
            </button>
            <div
              v-if="header.showCartPanel"
              class="overlayPanel overlayPanelCart"
              :class="{
                overlayPanelMoveDown: showProgressBar,
              }"
            >
              <IllustrationsCross
                class="overlayPanelClose"
                style="z-index: 8"
              />
              <button
                class="overlayPanelClose"
                style="z-index: 9"
                @click="hidePanels"
              />
              <div class="menuPanel flex-column align-items-start padding0 gap16 stretch">
                <div class="flex-row align-items-center padding0 gap16">
                  <div><IllustrationsCart /></div>
                  <div>
                    <h2 class="h bold">
                      {{ $t("bnpl:summary") }}
                    </h2>
                    <p class="p-body gray600">
                      {{ $d(new Date(header.cart.created), "longDateTime") }}
                    </p>
                  </div>
                </div>
                <p class="p-caption bold gray400">
                  {{ $t("bnpl:products") }}
                </p>
                <div class="flex-column align-items-start padding0 gap8 stretch">
                  <div
                    v-for="cartItem in header.cart.items"
                    class="flex-row align-items-start justify-content-space-between padding0 gap16 stretch grow"
                  >
                    <p class="p-caption gray600">
                      {{ cartItem.name }} ({{ cartItem.quantity }})
                    </p>
                    <p
                      class="p-caption bold noShrink"
                      style="text-align: right"
                    >
                      {{ $n(cartItem.price, "currency") }}
                    </p>
                  </div>
                </div>
                <p class="p-caption bold gray400">
                  {{ $t("bnpl:summaryOfPurchase") }}
                </p>
                <div class="flex-row align-items-center justify-content-space-between padding0 gap16 stretch noGrow">
                  <p class="p-caption">
                    {{ $t("bnpl:purchase") }}
                  </p>
                  <h2 class="h bold">
                    {{ $n(header.cart.sum, "currency") }}
                  </h2>
                </div>
                <template v-if="!bnplApplication.finance.usePayInFull">
                  <p class="p-caption bold gray400">
                    {{ $t("bnpl:schedule") }}
                  </p>
                  <div
                    class="flex-column align-items-start padding0 gap8 stretch noGrow"
                    style="color: var(--gray600)"
                  >
                    <div class="flex-row align-items-start justify-content-space-between padding0 gap16 stretch">
                      <p class="p-caption gray600">
                        {{ $t("bnpl:paymentSchedule") }}
                      </p>
                      <p
                        class="p-caption bold"
                        style="text-align: right"
                      >
                        {{ $t("bnpl:installments", { installments: bnplApplication.finance.installments }) }}
                      </p>
                    </div>
                    <div class="flex-row align-items-start justify-content-space-between padding0 gap16 stretch">
                      <p class="p-caption gray600">
                        {{ $t("bnpl:downPayment") }}
                      </p>
                      <p
                        class="p-caption bold"
                        style="text-align: right"
                      >
                        {{ $n(bnplApplication.deal.downPaymentValue, "currency") }}
                        ({{ $n(bnplApplication.deal.downPayment / 100, "percentMorePrecise") }})
                      </p>
                    </div>
                    <div class="flex-row align-items-start justify-content-space-between padding0 gap16 stretch">
                      <p class="p-caption gray600">
                        {{ $t("bnpl:interest") }}
                      </p>
                      <p
                        class="p-caption bold"
                        style="text-align: right"
                      >
                        {{ $n(bnplApplication.finance.interest / 100, "percentMorePrecise") }}
                      </p>
                    </div>
                    <div class="flex-row align-items-start justify-content-space-between padding0 gap16 stretch">
                      <p class="p-caption gray600">
                        {{ $t("bnpl:serviceFee") }}
                      </p>
                      <p
                        class="p-caption bold"
                        style="text-align: right"
                      >
                        {{ $n(bnplApplication.finance.serviceFee, "currency") }}
                      </p>
                    </div>
                    <div class="flex-row align-items-start justify-content-space-between padding0 gap16 stretch">
                      <p class="p-caption gray600">
                        {{ $t("bnpl:thm") }}
                      </p>
                      <p
                        class="p-caption bold"
                        style="text-align: right"
                      >
                        {{ $n(bnplApplication.finance.thm / 100, "percentMorePrecise") }}
                      </p>
                    </div>
                    <div class="flex-row align-items-start justify-content-space-between padding0 gap16 stretch noGrow">
                      <p class="p-caption gray600">
                        {{ $t("bnpl:subTotals") }}
                      </p>
                      <p
                        class="p-caption bold"
                        style="text-align: right"
                      >
                        {{ $n(bnplApplication.deal.transactions[1].sum, "currency") }}
                      </p>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>
          <button
            class="menuButton"
            type="button"
            @click="toggleHelp"
          >
            <div
              v-if="unreadCount && showMessageCount"
              class="unread-counter only-on-mobile"
            >
              {{ unreadCount }}
            </div>
            <IllustrationsMenuButtonHelpOff v-if="!header.showHelpPanel" />
            <IllustrationsMenuButtonHelpOn v-else />
          </button>
          <template v-if="chatStore.isChatEnabled && !isPrescoreLogin">
            <div
              v-if="header.showHelpPanel"
              class="overlayPanel chat"
              :class="{
                overlayPanelHelp: !limitHeader && !inCustomerLimit,
                overlayPanelMoveDown: showProgressBar,
                overlayPanelSave: limitHeader || inCustomerLimit,
              }"
            >
              <IllustrationsCross
                class="overlayPanelClose"
                style="z-index: 8"
              />
              <button
                class="overlayPanelClose"
                style="z-index: 9"
                @click="hidePanels"
              />
              <div class="menuPanel flex-column align-items-start padding0 gap16 stretch">
                <!-- <div v-if="askForHelpPage === 'main'" class="flex-column align-items-center padding0 gap32"> -->
                <HeaderChat />
              </div>
            </div>
          </template>
          <template v-else>
            <div
              v-if="header.showHelpPanel"
              class="overlayPanel"
              :class="{
                overlayPanelHelp: !limitHeader && !inCustomerLimit,
                overlayPanelMoveDown: showProgressBar,
                overlayPanelSave: limitHeader || inCustomerLimit,
              }"
            >
              <IllustrationsCross
                class="overlayPanelClose"
                style="z-index: 8"
              />
              <button
                class="overlayPanelClose"
                style="z-index: 9"
                @click="hidePanels"
              />
              <div class="menuPanel flex-column align-items-start padding0 gap16 stretch">
                <!-- <div v-if="askForHelpPage === 'main'" class="flex-column align-items-center padding0 gap32"> -->
                <div class="flex-column align-items-center padding0 gap32">
                  <div><IllustrationsHelp /></div>
                  <div class="flex-column align-items-start padding0 gap24 stretch">
                    <div class="flex-column align-items-start padding0 gap16">
                      <div class="flex-row justify-content-center stretch">
                        <h2 class="h bold flex-row stretch align-items-center">
                          {{ $t("bnpl:askForHelp") }}
                        </h2>
                      </div>
                      <p class="p-body gray600">
                        {{ $t("bnpl:askForHelpText") }}
                      </p>
                    </div>
                    <div class="flex-column align-items-center padding0 gap16 stretch">
                      <a
                        target="_blank"
                        :href="`https://m.me/${countryStore.messengerPageId}?ref=${publicId}`"
                      >
                        <div class="flex-row align-items-center padding0 gap24">
                          <div style="width: 80px; height: 80px">
                            <!-- <IllustrationsCall /> -->
                            <IconsFacebookMessenger />
                          </div>
                          <div class="flex-column align-items-start justify-content-center padding0 gap4 grow">
                            <p class="p-caption bold gray600">
                              {{ $t("bnpl:chat_on_facebook_messenger") }}
                            </p>
                            <a
                              class="btn btnM stretch"
                              target="_blank"
                              :href="`https://m.me/${countryStore.messengerPageId}?ref=${publicId}`"
                            >
                              {{ $t("bnpl:chat_on_facebook_messenger_button") }}
                            </a>
                          </div>
                        </div>
                      </a>
                      <!-- <p class="p-caption">
                        {{ $t("bnpl:or") }}
                      </p>
                      <button class="btn btnL stretch" @click="askForCallback">
                        {{ $t("bnpl:askForCallback") }}
                      </button> -->
                    </div>
                  </div>
                </div>
                <!-- <div
                  v-else-if="askForHelpPage === 'callBackForm'"
                  class="flex-column align-items-center padding0 gap32 stretch"
                >
                  <h2 class="h bold">
                    {{ $t("bnpl:askingForCallback") }}
                  </h2>
                  <div class="flex-column stretch gap8">
                    <div class="inputField">
                      <label class="p-caption bold">
                        {{ $t("bnpl:lastName") }}
                        <span class="bold" style="color: var(--systemError)">*</span>
                      </label>
                      <input v-model="callBackForm.lastName" class="textInput" />
                    </div>
                    <div class="inputField">
                      <label class="p-caption bold">
                        {{ $t("bnpl:firstName") }}
                        <span class="bold" style="color: var(--systemError)">*</span>
                      </label>
                      <input v-model="callBackForm.firstName" class="textInput" />
                    </div>
                    <div class="inputField">
                      <label class="p-caption bold">
                        {{ $t("bnpl:phone") }}
                        <span class="bold" style="color: var(--systemError)">*</span>
                      </label>
                      <input v-model="callBackForm.phone" class="textInput" />
                    </div>
                    <div class="inputField">
                      <label class="p-caption bold">
                        {{ $t("bnpl:emailAddress") }}
                        <span class="bold" style="color: var(--systemError)">*</span>
                      </label>
                      <input v-model="callBackForm.email" class="textInput" type="email" />
                      <p
                        v-if="callBackForm.email && !validateEmail(callBackForm.email)"
                        class="p-caption bold"
                        style="color: var(--systemError)"
                      >
                        Érvénytelen e-mail cím
                      </p>
                    </div>
                    <div class="inputField">
                      <label class="p-caption bold">
                        {{ $t("bnpl:preferredDate") }}
                        <span class="bold" style="color: var(--systemError)">*</span>
                      </label>
                      <InputCalendar :default-value="new Date()" @input="writeDate($event)" />
                    </div>
                    <div class="inputField">
                      <label class="p-caption bold">
                        {{ $t("bnpl:preferredTime") }}
                        <span class="bold" style="color: var(--systemError)">*</span>
                      </label>
                      <div class="flex-row justify-content-center align-items-center">
                        <input
                          v-model="callBackForm.preferredHour"
                          class="textInput timeInput"
                          type="number"
                          min="0"
                          max="23"
                        />
                        &nbsp;:&nbsp;
                        <input
                          v-model="callBackForm.preferredMinute"
                          class="textInput timeInput"
                          type="number"
                          min="0"
                          max="59"
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    class="btn btnL stretch"
                    :disabled="isCallBackFormFilled || !validateEmail(callBackForm.email)"
                    @click="sendCallBack"
                  >
                    {{ $t("bnpl:send") }}
                  </button>
                </div>
                <div
                  v-else-if="askForHelpPage === 'callBackSuccess'"
                  class="flex-column align-items-center padding0 gap32"
                >
                  <IllustrationsSuccess />
                  <div class="flex-column align-items-start padding0 gap24 stretch">
                    <div class="flex-column align-items-start padding0 gap16 stretch">
                      <div class="flex-row justify-content-center stretch">
                        <h2 class="h bold" style="color: var(--systemSuccess)">
                          {{ $t("bnpl:cbr.success") }}
                        </h2>
                      </div>
                      <p class="p-body gray600">
                        {{ $t("bnpl:askForCallbackSuccess") }}
                      </p>
                    </div>
                    <button class="btn btnL stretch" @click="hidePanels">
                      {{ $t("bnpl:close") }}
                    </button>
                  </div>
                </div>
                <div v-else-if="askForHelpPage === 'callBackFail'" class="flex-column align-items-center padding0 gap32">
                  <IllustrationsError />
                  <div class="flex-column align-items-start padding0 gap24 stretch">
                    <div class="flex-column align-items-start padding0 gap16 stretch">
                      <div class="flex-row justify-content-center stretch">
                        <h2 class="h bold" style="color: var(--systemError)">
                          {{ $t("bnpl:cbr.fail") }}
                        </h2>
                      </div>
                      <p class="p-body gray600">
                        {{ $t("bnpl:askForCallbackFail") }}
                      </p>
                    </div>
                    <button class="btn btnL stretch" @click="hidePanels">
                      {{ $t("bnpl:close") }}
                    </button>
                  </div>
                </div> -->
              </div>
            </div>
          </template>
          <template v-if="!isPrescore && !limitHeader && !inCustomerLimit">
            <button
              class="menuButton"
              type="button"
              @click="toggleSave"
            >
              <IllustrationsMenuButtonSaveOff v-if="!header.showSavePanel" />
              <IllustrationsMenuButtonSaveOn v-else />
            </button>
            <div
              v-if="header.showSavePanel"
              class="overlayPanel overlayPanelSave"
              :class="{
                overlayPanelMoveDown: showProgressBar,
              }"
            >
              <IllustrationsCross
                class="overlayPanelClose"
                style="z-index: 8"
              />
              <button
                class="overlayPanelClose"
                style="z-index: 9"
                @click="hidePanels"
              />
              <div class="flex-column align-items-center padding0 gap32">
                <template v-if="header.saveStatus === 0">
                  <IllustrationsSave />
                  <div class="flex-column align-items-start padding0 gap24 stretch">
                    <div class="flex-column align-items-start padding0 gap16">
                      <div class="flex-row justify-content-center stretch">
                        <h2 class="h bold flex-row align-items-center stretch">
                          {{ $t("bnpl:saveProcess") }}
                        </h2>
                      </div>
                      <p class="p-body gray600">
                        {{ $t("bnpl:saveProcessText") }}
                      </p>
                    </div>
                  </div>
                  <div class="flex-column align-items-start justify-content-center padding0 gap24 stretch">
                    <div class="inputField stretch">
                      <label
                        class="textBold"
                        for="mail"
                      >{{ $t("bnpl:askForEmail") }}</label>
                      <input
                        id="mail"
                        v-model="header.customerEmail"
                        type="email"
                        class="textInput stretch"
                        :placeholder="$t('bnpl:email_placeholder')"
                        @input="removeInvalidText"
                      >
                      <p
                        v-if="!header.emailIsValid"
                        class="p-caption bold"
                        style="color: var(--systemError)"
                      >
                        Érvénytelen e-mail cím
                      </p>
                    </div>
                    <button
                      :disabled="header.customerEmail?.length === 0"
                      class="btn btnL stretch"
                      @click="sendSaveEmail"
                    >
                      {{ $t("bnpl:save") }}
                    </button>
                  </div>
                </template>
                <template v-else-if="header.saveStatus === 1">
                  <IllustrationsSuccess />
                  <div class="flex-column align-items-start padding0 gap24 stretch">
                    <div class="flex-column align-items-start padding0 gap16 stretch">
                      <div class="flex-row justify-content-center stretch">
                        <h2
                          class="h bold"
                          style="color: var(--systemSuccess)"
                        >
                          {{ $t("bnpl:save.success") }}
                        </h2>
                      </div>
                      <p class="p-body gray600">
                        {{ $t("bnpl:saveProcessSuccess") }}
                      </p>
                    </div>
                    <button
                      class="btn btnL stretch"
                      @click="hidePanels"
                    >
                      {{ $t("bnpl:close") }}
                    </button>
                  </div>
                </template>
                <template v-else-if="header.saveStatus === 2">
                  <IllustrationsError />
                  <div class="flex-column align-items-start padding0 gap24 stretch">
                    <div class="flex-column align-items-start padding0 gap16 stretch">
                      <div class="flex-row justify-content-center stretch">
                        <h2
                          class="h bold"
                          style="color: var(--systemError)"
                        >
                          {{ $t("bnpl:save.fail") }}
                        </h2>
                      </div>
                      <p class="p-body gray600">
                        {{ $t("bnpl:saveProcessFail") }}
                      </p>
                    </div>
                    <button
                      class="btn btnL stretch"
                      @click="hidePanels"
                    >
                      {{ $t("bnpl:close") }}
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div
        v-if="showProgressBar"
        id="bottomProgress"
        class="flex-column align-items-center gap8 grow"
      >
        <template v-if="!isPrescore && !isB2bPrescore">
          <p class="p-small">
            {{ $t("bnpl:step", { current: currentStep, count: stepCount }) }} ({{
              formatStepTime(currentStepDuration, countryStore.locale)
            }})
          </p>
          <div style="width: 90%; max-width: 612px">
            <ProgressBar
              :max="stepCount"
              :value="currentStep"
            />
          </div>
        </template>
        <template v-else>
          <p class="p-small">
            {{ $t("bnpl:step", { current: prescoreCurrentStep, count: prescoreStepCount }) }} ({{
              formatStepTime(currentStepDuration, countryStore.locale)
            }})
          </p>
          <div style="width: 100%; max-width: 612px">
            <ProgressBar
              :max="prescoreStepCount"
              :value="prescoreCurrentStep"
            />
          </div>
        </template>
      </div>
    </div>
  </client-only>
</template>

<script setup>
import { useBrandingStore } from "~/stores/branding";
import { useCartStore } from "~/stores/cart";
import { useSessionStore } from "~/stores/session";
import { useBnplApplicationStore } from "~/stores/bnplapplication";
import { useUserStore } from "~/stores/user";
import Network from "~/utils/network";
import { formatDate, formatDecimalSeparator, maskValue, validateEmail, formatStepTime } from "~/utils/util";
import { useLimitStore } from "~~/stores/limit";
import { useCountryStore } from "~~/stores/country";
import { useChatStore } from "~~/stores/chat";
import { useB2bFlowStore } from "~/stores/b2bFlow";

const isB2b = computed(() => route.name === "b2b-financier-publicId" || route.name === "kyc-financier-publicId");
const isManagerKyc = computed(() => route.name === "kyc-financier-publicId");
const brandingStore = useBrandingStore();
const cartStore = useCartStore();
const sessionStore = useSessionStore();
// const b2bFlowStore = useB2bFlowStore();
// const b2cFlowStore = useBnplApplicationStore();
const userStore = useUserStore();
const limitStore = useLimitStore();
const countryStore = useCountryStore();
const chatStore = useChatStore();

let bnplApplicationStore;

const limitPositionNumber = computed(() => limitStore.getPositionNumber);

const route = useRoute();
const isPrescore = computed(() => route.name === "financier-prescore" || route.name === "financier-prescore-prescoreId");
const isB2bPrescore = computed(() => route.name === "b2b-financier-prescore-prescoreId" || route.name === "b2b-financier-prescore");

const bnplApplication = computed(() => bnplApplicationStore.bnplApplication);
const currentStep = computed(() => bnplApplicationStore.currentStep());
const stepCount = computed(() => bnplApplicationStore.stepCount());
const showProgressBar = computed(() => bnplApplicationStore.showProgressBar);
const currentStepDuration = computed(() => bnplApplicationStore.currentStepDuration());
const publicId = computed(() => bnplApplicationStore.publicId);

const isPrescoreLogin = computed(() => route.name === "financier-prescore" || route.name === "b2b-financier-prescore");

const showCartButton = computed(() => sessionStore.isLoggedIn && !isB2bPrescore.value);

const askForHelpPage = ref("main");

const isCallBackFormFilled = ref(false);

const prescoreStepCount = computed(() => isB2bPrescore.value ? stepCount.value : stepCount.value - 2);

const inCustomerLimit = computed(() => ["financier-customer-limit", "financier-customer-limit-result"].includes(route.name));

const prescoreCurrentStep = computed(() => {
  if (isB2bPrescore.value) {
    if (stepCount.value >= currentStep.value) {
      return currentStep.value;
    } else {
      return stepCount.value;
    }
  } else {
    if (stepCount.value - 2 >= currentStep.value) {
      return currentStep.value;
    } else {
      return stepCount.value - 2;
    }
  }
});

const currentBotMessageCount = computed(() => chatStore.history.filter(message => message.sentBy === "BOT").length)
const lastSavedChatMessageCount = ref(null);
const unreadCount = computed(() => {
  const count = currentBotMessageCount.value - lastSavedChatMessageCount.value;
  if (count < 0) {
    return 0;
  } else {
    return count;
  }
});
const showMessageCount = ref(false);

watch(
  lastSavedChatMessageCount,
  (value) => {
    sessionStorage.setItem("last-saved-chat-message-count", value);
  }
)

watch(
  currentBotMessageCount,
  () => {
    if (header.showHelpPanel) {
      lastSavedChatMessageCount.value = currentBotMessageCount.value;
    }
  }
)

function checkCallBackFormFilled() {
  callBackForm.phone = maskValue(FIELD_MASK.PHONE, callBackForm.phone);
  callBackForm.preferredHour = callBackForm.preferredHour?.toString()?.slice(0, 2);
  callBackForm.preferredMinute = callBackForm.preferredMinute?.toString()?.slice(0, 2);
  isCallBackFormFilled.value = Object.values(callBackForm).some((e) => !e);
}

const callBackForm = reactive({
  firstName: userStore.customerFirstName ?? "",
  lastName: userStore.customerLastName ?? "",
  email: userStore.customerEmail ?? "",
  phone: maskValue(FIELD_MASK.PHONE, userStore.customerPhone),
  preferredHour: "14",
  preferredMinute: "00",
  preferredDate: null,
});

watch(callBackForm, checkCallBackFormFilled, { deep: true });

onBeforeMount(async () => {
  if (route.params.financier) {
    chatStore.getConfig(route.params.financier);
  }
  if (isB2b.value) {
    bnplApplicationStore = useB2bFlowStore();
  } else {
    bnplApplicationStore = useBnplApplicationStore();
  }
});

onMounted(() => {
  checkCallBackFormFilled();
  lastSavedChatMessageCount.value = sessionStorage.getItem("last-saved-chat-message-count");
  setTimeout(() => {
    showMessageCount.value = true;  
  }, 5000);
});

const header = reactive({
  logoBigUrl: brandingStore.logoBigUrl,
  cart: cartStore.cart,
  showCartPanel: false,
  showHelpPanel: false,
  showSavePanel: false,
  customerEmail: userStore.customerEmail,
  emailIsValid: true,
  saveStatus: 0,
});

function toggleCart() {
  header.showHelpPanel = false;
  header.showSavePanel = false;
  header.showCartPanel = !header.showCartPanel;
}

function toggleHelp() {
  header.showCartPanel = false;
  header.showSavePanel = false;
  header.showHelpPanel = !header.showHelpPanel;
  askForHelpPage.value = "main";
  lastSavedChatMessageCount.value = currentBotMessageCount.value;
}

function toggleSave() {
  header.showCartPanel = false;
  header.showHelpPanel = false;
  header.showSavePanel = !header.showSavePanel;
}
function hidePanels() {
  header.showHelpPanel = false;
  header.showSavePanel = false;
  header.showCartPanel = false;
  header.saveStatus = 0;
  header.customerEmail = "";
  askForHelpPage.value = "main";
}

function storeSendSaveEmail(options) {
  return bnplApplicationStore.sendSaveEmail(options);
}

function sendSaveEmail() {
  if (validateEmail(header.customerEmail)) {
    storeSendSaveEmail({ email: header.customerEmail }).then(
      () => {
        header.saveStatus = 1;
      },
      () => {
        header.saveStatus = 2;
      }
    );
  } else {
    header.emailIsValid = false;
  }
}

function removeInvalidText() {
  header.emailIsValid = true;
}

async function cancelApplication() {
  if (sessionStore.isLoggedIn) {
    await bnplApplicationStore.close();
  } else {
    await bnplApplicationStore.closePublic();
  }
  hidePanels();
  await sessionStore.logout();
  window.open(cartStore.cart.redirectUrl, "_self");
}

function askForCallback() {
  askForHelpPage.value = "callBackForm";
}

async function sendCallBack() {
  try {
    let url = sessionStore.isLoggedIn ? "/call-me-back" : "/call-me-back-public";
    await Network.http(url, {
      method: "POST",
      body: {
        email: callBackForm.email,
        phone: callBackForm.phone,
        firstName: callBackForm.firstName,
        lastName: callBackForm.lastName,
        preferredDate: callBackForm.preferredDate,
        preferredTime:
          callBackForm.preferredHour.toString().padStart(2, "0") +
          ":" +
          callBackForm.preferredMinute.toString().padStart(2, "0"),
        type: "CBR_BTN",
        origin: "BNPL",
        publicId: publicId.value,
      },
    });
    askForHelpPage.value = "callBackSuccess";
  } catch (e) {
    askForHelpPage.value = "callBackFail";
  }
}

function writeDate(value) {
  callBackForm.preferredDate = formatDate(value);
}

const limitHeader = computed(() => {
  return route.name === "financier-limit";
});
</script>

<style scoped>
.appHeader {
  top: 0;
  left: 0;
  position: sticky;
  width: 100%;
  height: fit-content;
  background-color: white;
  padding: 8px 16px;
  z-index: 10;
}
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(18, 18, 18, 0.4);
  z-index: 9;
}

.logo-big {
  max-width: 176px;
  max-height: 44px;
}

#bottomProgress {
  display: none;
}

#upperProgress {
  display: flex;
}

@media only screen and (max-width: 792px) {
  #bottomProgress {
    display: flex;
  }

  #upperProgress {
    display: none;
  }
}

.menuButtonContainer {
  margin-left: auto;
  width: 200px;
  justify-content: end;
  gap: 16px;
}

@media only screen and (max-width: 792px) {
  .menuButtonContainer {
    width: 140px;
    gap: 8px;
  }

  .appHeader {
    padding: 8px;
  }
}
</style>

<style>
.overlayPanel {
  position: fixed;
  width: 343px;
  z-index: 10;
  right: 16px;
  top: 76px;
  background: white;
  border-radius: 8px;
  padding: 24px;
  isolation: isolate;
}

.overlayPanel::before {
  content: "";
  display: block;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 11px solid white;
  position: absolute;
  top: -10px;
}

.overlayPanelCart::before {
  right: 124px;
}

.overlayPanelHelp::before {
  right: 66px;
}

.overlayPanelSave::before {
  right: 12px;
}

@media only screen and (max-width: 792px) {
  .overlayPanelMoveDown {
    top: 116px;
  }

  .overlayPanel {
    width: calc(100% - 20px);
    left: 10px;
    right: 10px;

  }

  .overlayPanel.chat {
    bottom: 10px;
  }
}

.overlayPanel.chat .menuPanel {
  height: 100%;
}

.overlayPanel.chat {
  bottom: 20px;
}

.overlayPanelClose {
  padding: 0;
  background: transparent;
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  width: 24px;
  height: 24px;
  border: none;
}

.timeInput {
  width: 4em;
  text-align: center;
  /* Firefox */
  appearance: textfield;
  -moz-appearance: textfield;
}

/* Chrome, Safari, Edge, Opera */
.timeInput::-webkit-outer-spin-button,
.timeInput::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>

<style scoped lang="scss">
.unread-counter {
  position: absolute;
  top: -7px;
  right: -7px;
  box-sizing: border-box;
  min-width: 18px;
  min-height: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 6px;
  border-radius: 999999999px;
  background: var(--black);
  color: white;
  font-size: 10px;
  font-weight: 700;
  line-height: 7px;
}

.only-on-mobile {
  display: none;
}

@media only screen and (max-width: 792px) {
  .only-on-mobile {
    display: flex;
  }
}
</style>
